import React from "react";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import PaperLayout from "components/PaperLayout/PaperLayout";
import SimpleDataTable from "components/SimpleDataTable/SimpleDataTable";
import { DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent";
import { Refresh } from "components/DataBaseComponent/DataBaseComponent";
import ActionList from "components/ActionList";
import ActionButton from "components/Button";
import repository from "client/repository";
import { HostedScriptsRolloutResource } from "client/resources/hostedScriptsRolloutResource";
import { HostedScriptsRolloutState } from "client/resources/hostedScriptsRolloutState";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import DateFormatter from "utils/DateFormatter";
import routeLinks from "routeLinks";

interface Props {
    reefId: string;
    breadcrumbTitle?: string;
}

interface State extends DataBaseComponentState {
    rollouts?: HostedScriptsRolloutResource[];
}

class RolloutHistoryTable extends SimpleDataTable<HostedScriptsRolloutResource> {
}

export class ReefHostedScriptsRollouts extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            this.doRefresh = await this.startRefreshLoop(() => this.loadData(), 30000);
        });
    }

    render() {
        return <PaperLayout title="Hosted Scripts"
                            breadcrumbTitle={this.props.breadcrumbTitle}
                            busy={this.state.busy}
                            errors={this.state.errors}
                            sectionControl={<ActionList actions={[
                                <ActionButton label="Refresh" onClick={this.doRefresh}/>
                            ]}/>}>
            {this.state.rollouts &&
                <RolloutHistoryTable
                    data={this.state.rollouts}
                    headerColumns={["Started", "Version", "Cluster", "Outcome", "Took", "Task"]}
                    onRow={this.buildRow}
                    onEmpty={<div>No hosted scripts rollouts recorded for this reef</div>}/>}
        </PaperLayout>;
    }

    private doRefresh: Refresh = () => Promise.resolve();

    private async loadData() {
        return {rollouts: await repository.HostedScripts.listRollouts({reefId: this.props.reefId})};
    }

    private buildRow = (rollout: HostedScriptsRolloutResource) => [
        DateFormatter.dateToShortFormat(rollout.StartedAt),
        <code>{rollout.Version}</code>,
        rollout.KubernetesClusterName,
        outcome(rollout),
        took(rollout),
        <InternalLink to={routeLinks.backgroundTasks.task(rollout.BackgroundTaskId).root}>
            {rollout.BackgroundTaskId}
        </InternalLink>,
    ];
}

function outcome(rollout: HostedScriptsRolloutResource) {
    switch (rollout.State) {
        case HostedScriptsRolloutState.Completed:
            return <span style={{color: themeTokens.color.text.success}}>
                Completed {DateFormatter.dateToShortFormat(rollout.CompletedAt)}
            </span>;
        case HostedScriptsRolloutState.Failed:
            return <span style={{color: themeTokens.color.text.danger}}>
                Failed {DateFormatter.dateToShortFormat(rollout.FailedAt)}
            </span>;
        default:
            return <span style={{color: themeTokens.color.text.info}}>Running</span>;
    }
}

function took(rollout: HostedScriptsRolloutResource) {
    const finished = rollout.CompletedAt ?? rollout.FailedAt;
    return finished ? DateFormatter.momentFrom(finished, rollout.StartedAt) : null;
}

export default ReefHostedScriptsRollouts;
